import React from 'react';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#5865F2';
  const lastUpdated = 'May 6, 2026';

  return (
    <LegalLayout app="Open Claw Discord Application" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Overview
              </h2>
              <p className="leading-relaxed">
                This Privacy Policy explains how Feral Techlabs handles information when you interact
                with the Open Claw Discord application (the "Application"). Open Claw is used as a
                communication and operations interface for AI-assisted workflows, infrastructure status,
                software delivery coordination, and related internal automation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We May Process
              </h2>
              <p className="mb-4 leading-relaxed">
                Depending on how the Application is configured, we may process limited operational data such as:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Discord server, channel, thread, and message identifiers</li>
                <li>Message content sent to the Application</li>
                <li>Usernames, display names, and Discord user IDs needed to route replies</li>
                <li>Commands, task prompts, reactions, and bot-interaction metadata</li>
                <li>Operational logs required for reliability, security, and debugging</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                How We Use Information
              </h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To deliver bot responses and workflow updates inside Discord</li>
                <li>To route messages to the correct internal agent or service</li>
                <li>To monitor system health, failures, and delivery status</li>
                <li>To improve reliability, safety, and operational quality of the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do Not Sell
              </h2>
              <p className="leading-relaxed">
                We do not sell personal information. We do not use Discord message data for advertising,
                behavioral profiling, or third-party data brokerage.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Retention
              </h2>
              <p className="leading-relaxed">
                We retain only the data reasonably necessary to operate the Application, maintain security,
                investigate failures, and preserve internal task context. Retention periods may vary based on
                operational requirements, backups, and audit needs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                The Application depends on third-party platforms and infrastructure including Discord and any
                configured AI/model or hosting providers. Your use of Discord itself is governed by Discord's
                own terms and privacy practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Security
              </h2>
              <p className="leading-relaxed">
                We take reasonable measures to protect operational data and access credentials. However, no
                system can be guaranteed perfectly secure, and you should avoid sending highly sensitive
                credentials or secrets through ordinary Discord chat unless you explicitly accept that risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Choices
              </h2>
              <p className="leading-relaxed">
                You can stop using the Application at any time by removing it from a server, revoking access,
                or discontinuing interaction. For privacy-related questions or requests, contact Feral Techlabs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact
              </h2>
              <p className="leading-relaxed">
                If you have questions about this Privacy Policy, contact us at{' '}
                <a href="mailto:tanushgupta11@gmail.com" style={{ color: accentColor }} className="transition-colors">
                  tanushgupta11@gmail.com
                </a>
                .
              </p>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
